import { CabeceraSeniat } from "./CabeceraSeniat"
import { PieResponsable } from "./PieResponsable"
import { Caja, Rotulo } from "./piezas"
import type { Comun, DatosVehiculos } from "./tipos"
import { NOMBRES } from "./tipos"

/**
 * «Vehículos Confrontados 2026» — la segunda hoja del formato 10.2.
 *
 * ⚠ **Es un formato POR MODELO, no por expediente**, y lo dice su propia nota:
 * «LLENAR 1 FORMATO POR MODELO DE VEHICULO». Por eso el bloque del vehículo tiene una
 * sola fila: quien despacha tres modelos imprime tres hojas. Convertirlo en una tabla
 * de filas sería más cómodo aquí y rechazado allá.
 *
 * ⚠ **El año lleva 2026 en el título porque el formato lo lleva.** Es el año de
 * vigencia que publica la aduana, no la fecha del documento que se rellena; cuando
 * publiquen el de 2027 se añade otra plantilla y ésta se queda para reimprimir lo
 * viejo — igual que un documento del SGI no se reescribe, se versiona.
 */
export function VehiculosConfrontados({
  datos,
  onCambio,
}: {
  datos: DatosVehiculos
  onCambio: (cambio: Partial<DatosVehiculos>) => void
}) {
  const comun = (cambio: Partial<Comun>) => onCambio(cambio as Partial<DatosVehiculos>)

  return (
    <>
      <CabeceraSeniat titulo={NOMBRES.VEHICULOS} datos={datos} onCambio={comun} />

      {/* Quién declara y para quién. */}
      <Rotulo x={151.4} y={118.84}>
        DECLARANTE
      </Rotulo>
      <Caja
        x={62.5}
        y={130.5}
        w={239.2}
        h={17.8}
        etiqueta="Declarante"
        valor={datos.declarante}
        onCambio={(declarante) => onCambio({ declarante })}
      />

      <Rotulo x={401.9} y={118.54}>
        CONSIGNATARIO
      </Rotulo>
      <Caja
        x={312.6}
        y={130.2}
        w={250.3}
        h={19.5}
        etiqueta="Consignatario"
        valor={datos.consignatario}
        onCambio={(consignatario) => onCambio({ consignatario })}
      />

      {/* La DUA y los valores. */}
      <Rotulo x={75} y={155.64}>
        N° DE DUA
      </Rotulo>
      <Caja
        x={65}
        y={168}
        w={72.6}
        h={21}
        etiqueta="N° de DUA"
        valor={datos.dua}
        onCambio={(dua) => onCambio({ dua })}
      />

      <Rotulo x={166.7} y={155.64}>
        FECHA
      </Rotulo>
      <Caja
        x={150.4}
        y={167.3}
        w={68.1}
        h={21}
        centrado={true}
        etiqueta="Fecha"
        valor={datos.fecha}
        onCambio={(fecha) => onCambio({ fecha })}
      />

      <Rotulo x={236.4} y={155.64}>
        VALOR CIF $
      </Rotulo>
      {/* `tabular` en los tres importes (regla 10): sin ella, dos hojas seguidas no
          alinean los dígitos y el bloque se lee torcido. */}
      <Caja
        x={227}
        y={167.3}
        w={74.6}
        h={21}
        cifra={true}
        etiqueta="Valor CIF en dólares"
        valor={datos.valor_cif}
        onCambio={(valor_cif) => onCambio({ valor_cif })}
      />

      <Rotulo x={311.9} y={156.44}>
        TIPO DE CAMBIO
      </Rotulo>
      <Caja
        x={315.4}
        y={168.8}
        w={67.9}
        h={19.5}
        cifra={true}
        etiqueta="Tipo de cambio"
        valor={datos.tipo_cambio}
        onCambio={(tipo_cambio) => onCambio({ tipo_cambio })}
      />

      <Rotulo x={418.5} y={155.64}>
        VALOR EN ADUANA (BS. S)
      </Rotulo>
      <Caja
        x={397.2}
        y={168.8}
        w={165.7}
        h={19.5}
        cifra={true}
        etiqueta="Valor en aduana en bolívares"
        valor={datos.valor_aduana}
        onCambio={(valor_aduana) => onCambio({ valor_aduana })}
      />

      {/* El vehículo. */}
      <Rotulo x={249.7} y={196.44}>
        DATOS DEL VEHÍCULO
      </Rotulo>

      <Rotulo x={132.7} y={214.44}>
        MARCA
      </Rotulo>
      <Caja
        x={112.5}
        y={229.4}
        w={74.6}
        h={21}
        etiqueta="Marca"
        valor={datos.marca}
        onCambio={(marca) => onCambio({ marca })}
      />

      <Rotulo x={225.6} y={215.44}>
        MODELO
      </Rotulo>
      <Caja
        x={212.3}
        y={230.8}
        w={74.6}
        h={21}
        etiqueta="Modelo"
        valor={datos.modelo}
        onCambio={(modelo) => onCambio({ modelo })}
      />

      <Rotulo x={326.5} y={215.44}>
        AÑO
      </Rotulo>
      <Caja
        x={301.7}
        y={229.4}
        w={74.6}
        h={21}
        centrado={true}
        etiqueta="Año del vehículo"
        valor={datos.anio}
        onCambio={(anio) => onCambio({ anio })}
      />

      <Rotulo x={409} y={216.64}>
        CANTIDAD
      </Rotulo>
      <Caja
        x={399}
        y={230.8}
        w={74.6}
        h={21}
        centrado={true}
        cifra={true}
        etiqueta="Cantidad"
        valor={datos.cantidad}
        onCambio={(cantidad) => onCambio({ cantidad })}
      />

      <PieResponsable tipo="VEHICULOS" datos={datos} onCambio={comun} />
    </>
  )
}
